import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Impact = () => {
  const caseStudies = [
    {
      tag: 'Knowledge Management Platform',
      problem: 'Founder had years of scattered documents, notes, and resources. Finding information took hours, causing missed deadlines and constant frustration. No clear technical path forward.',
      outcome: 'Built custom dashboard with Glean-powered AI chatbot. Plain English search across all documents. Instant answers in seconds, not hours.',
      stats: [{ val: '90%', lbl: 'Faster retrieval' }, { val: '5 sec', lbl: 'Avg search time' }],
    },
    {
      tag: 'Developer Community Platform',
      problem: 'Founder wanted marketplace for developers to connect and collaborate on passion projects. Had vision but no technical background to execute.',
      outcome: 'Complete portal with developer profiles, interest matching, project boards, and messaging. Every decision explained in plain English.',
      stats: [{ val: '200+', lbl: 'Active developers' }, { val: '8 wks', lbl: 'Idea to launch' }],
    },
    {
      tag: 'Health & Wellness App Rescue',
      problem: 'Founder lost access to MVP when developer disappeared. Had paying customers but couldn\'t update app or fix bugs. Complete uncertainty about how to recover.',
      outcome: 'Rebuilt from scratch with proper documentation. Restored code ownership, added requested features, provided training for future development.',
      stats: [{ val: '6 wks', lbl: 'Rebuilt & deployed' }, { val: '100%', lbl: 'Code ownership' }],
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Our Impact — BrightWings</title>
        <meta name="description" content="Founders don't fail because of vision — they fail because they can't confidently direct technical execution. See how BrightWings changes that." />
        <link rel="canonical" href="https://brightwings.io/impact" />
        <meta property="og:url" content="https://brightwings.io/impact" />
        <meta property="og:title" content="Our Impact — BrightWings" />
        <meta property="og:description" content="Founders don't fail because of vision — they fail because they can't confidently direct technical execution. See how BrightWings changes that." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Impact — BrightWings" />
        <meta name="twitter:description" content="Founders don't fail because of vision — they fail because they can't confidently direct technical execution. See how BrightWings changes that." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Our Impact</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Founders don't fail because of <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>vision.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '540px', margin: '0 auto' }}>
              They fail because they can't confidently direct technical execution. We change that.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '24px' }}>
              The founder gap is real
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '28px', lineHeight: 1.7 }}>
              Most founders cannot confidently direct technical execution. This leaves them vulnerable to:
            </p>
            <ul className="space-y-3 mb-10">
              {[
                'Struggling to evaluate developers and proposals',
                'Not knowing what "good architecture" looks like',
                'Feeling dependent on whoever is building',
                'Making decisions without understanding tradeoffs',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2.5" style={{ backgroundColor: 'var(--bw-blue)' }} />
                  <span style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.6 }}>{item}</span>
                </li>
              ))}
            </ul>
            <div className="bw-card">
              <div className="bw-card-header">
                <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--bw-dark)', fontSize: '17px' }}>The result?</p>
              </div>
              <div className="bw-card-body">
                <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
                  Expensive missteps. Fragile products. Loss of control.
                </p>
                <p style={{ fontSize: '17px', color: 'var(--bw-blue)', fontWeight: 600, marginTop: '12px' }}>
                  BrightWings exists to eliminate that uncertainty.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guide positioning */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl">
            <span className="bw-eyebrow">Where BrightWings comes in</span>
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '20px' }}>
              You don't need more tools. You need a guide who's been here before.
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.7, marginBottom: '32px' }}>
              We've worked with founders from every background — solo creators, first-time entrepreneurs, operators pivoting into tech. The struggle is always the same: brilliant idea, no roadmap. BrightWings exists to be the partner you needed from the start. Not a vendor. Not a dev shop. A guide who helps you decide what to build, why to build it, and how to get it done without losing your vision in the process.
            </p>
            <div className="flex gap-12 flex-wrap">
              {[
                { val: '15+', lbl: 'Years of product and engineering experience' },
                { val: '50+', lbl: 'Founders Helped' },
                { val: '100%', lbl: 'Code ownership transferred to you' },
              ].map((s, i) => (
                <div key={i}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '36px', color: 'var(--bw-blue)', letterSpacing: '-0.03em', lineHeight: 1 }}>{s.val}</div>
                  <div style={{ fontSize: '13px', color: 'var(--bw-muted)', marginTop: '4px' }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="bw-eyebrow">Real Outcomes</span>
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)' }}>
              Founders who built with direction.
            </h2>
          </div>
          <div className="space-y-8">
            {caseStudies.map((cs, i) => (
              <div key={i} className="bw-card">
                <div className="bw-card-header">
                  <span className="bw-tag-soft">{cs.tag}</span>
                </div>
                <div className="bw-card-body">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>The challenge</p>
                      <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{cs.problem}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>The outcome</p>
                      <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px', marginBottom: '20px' }}>{cs.outcome}</p>
                      <div className="flex gap-6">
                        {cs.stats.map((stat, j) => (
                          <div key={j}>
                            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '24px', color: 'var(--bw-blue)', letterSpacing: '-0.02em' }}>{stat.val}</div>
                            <div style={{ fontSize: '12px', color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)' }}>{stat.lbl}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Real Outcome */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '32px' }}>The Real Outcome</h2>
            <div className="space-y-4 mb-10">
              {[
                "You don't feel dependent on developers.",
                "You don't guess your way through architecture decisions.",
                "You don't build yourself into a corner.",
              ].map((item, i) => (
                <div key={i} className="bw-callout">{item}</div>
              ))}
            </div>
            <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bw-dark)', marginBottom: '20px' }}>Instead:</p>
            <ul className="space-y-4">
              {['You build with clarity.', 'You scale with intention.', 'You operate like a disciplined product company from day one.'].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle size={20} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '17px', fontWeight: 500, color: 'var(--bw-dark)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>Build with direction. Ship with confidence.</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Technical direction for founders who refuse to build blind.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Start a conversation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Impact;
